import { useEffect, useRef, useState, useCallback } from 'react'

/**
 * Tracks a value and returns a debounced copy that only updates after the
 * given delay has passed without the input changing.
 *
 * Replaces ad-hoc patterns like:
 *   clearTimeout((window as any).__contactSearchTimer)
 *   ;(window as any).__contactSearchTimer = setTimeout(() => setX(v), 300)
 * which leak through `window` and don't tear down on unmount.
 */
export function useDebouncedValue<T>(value: T, delayMs = 300): T {
  const [debounced, setDebounced] = useState<T>(value)
  const timerRef = useRef<number | null>(null)

  useEffect(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current)
    }
    timerRef.current = window.setTimeout(() => {
      setDebounced(value)
      timerRef.current = null
    }, delayMs)
    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
    }
  }, [value, delayMs])

  return debounced
}

/**
 * Returns a debounced wrapper around a callback. Subsequent calls within
 * `delayMs` reset the timer and only the latest invocation runs.
 *
 * Use when you don't want to track an intermediate value in state — e.g.
 * scroll handlers, fire-and-forget API pings.
 */
export function useDebouncedCallback<TArgs extends unknown[]>(
  fn: (...args: TArgs) => void,
  delayMs = 300,
): (...args: TArgs) => void {
  const timerRef = useRef<number | null>(null)
  const fnRef = useRef(fn)

  // Keep the latest callback in a ref so callers don't have to memoize it.
  useEffect(() => {
    fnRef.current = fn
  }, [fn])

  useEffect(() => {
    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
    }
  }, [])

  return useCallback(
    (...args: TArgs) => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current)
      }
      timerRef.current = window.setTimeout(() => {
        timerRef.current = null
        fnRef.current(...args)
      }, delayMs)
    },
    [delayMs],
  )
}
